import { playAlertSound } from "./play-alert-sound";

const SOUND_LEASE_MS = 5_000;

const SOUND_TAB_ID =
  typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface LockManagerLike {
  request: (
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: unknown | null) => void | Promise<void>,
  ) => Promise<void>;
}

interface SoundLease {
  owner: string;
  expiresAt: number;
}

/**
 * Play the attention sound in exactly ONE tab.
 *
 * Several tabs may hold the same alert scope; without this they would all chime
 * together. A Web Lock elects the single player where available, with a
 * localStorage lease as the fallback for browsers without `navigator.locks`.
 *
 * Lives here rather than in `use-alerts-live` because both the first chime (the
 * live channel's attention batch) and every repeat (the app-wide alarm in the
 * dashboard layout) have to elect the same single player.
 */
export async function playAttentionAsLeader(scopeKey: string): Promise<void> {
  if (typeof window === "undefined" || typeof navigator === "undefined") return;

  // `settle` guards against two tabs writing the lease at the same instant. Under
  // a Web Lock that race cannot happen, so the wait is skipped — it is a
  // setTimeout, and on a hidden tab Chrome can stretch it to ~a minute, which is
  // exactly when the alarm most needs to be prompt.
  const acquireLeaseAndPlay = async (settle: boolean) => {
    try {
      const key = `minemaestro-alert-sound-leader:${scopeKey}`;
      const now = Date.now();
      const current = JSON.parse(localStorage.getItem(key) ?? "null") as SoundLease | null;
      if (current && current.owner !== SOUND_TAB_ID && current.expiresAt > now) return;

      const lease: SoundLease = { owner: SOUND_TAB_ID, expiresAt: now + SOUND_LEASE_MS };
      localStorage.setItem(key, JSON.stringify(lease));
      if (settle) {
        // Let simultaneous fallback contenders settle; only the final owner plays.
        await new Promise<void>((resolve) => setTimeout(resolve, 25));
      }
      const accepted = JSON.parse(localStorage.getItem(key) ?? "null") as SoundLease | null;
      if (accepted?.owner === SOUND_TAB_ID) playAlertSound();
    } catch {
      // Cross-tab leadership is unavailable; do not risk duplicate sound.
    }
  };

  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  if (locks) {
    await locks.request(`minemaestro-alert-sound:${scopeKey}`, { ifAvailable: true }, async (lock) => {
      // The lock already guarantees exclusivity — no settle wait needed.
      if (lock) await acquireLeaseAndPlay(false);
    });
    return;
  }

  await acquireLeaseAndPlay(true);
}

/**
 * Lock scope for the repeat alarm. The notify store holds ONE alert scope at a
 * time app-wide, so every tab must contend for the same name — a per-location
 * key would let two tabs on different locations chime over each other.
 */
export const ALARM_SCOPE_KEY = "alarm";
